import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, downloadReportExport } from "../lib/api";
const CASE_STATUSES = [
    "NEW_CASE", "DOCUMENTS_PENDING", "FILED", "COURT_PROCEEDINGS", "JUDGMENT", "ENFORCEMENT",
    "SETTLEMENT_NEGOTIATION", "RECOVERY_SETTLEMENT", "PARTIALLY_RECOVERED", "FULLY_RECOVERED",
    "ON_HOLD", "WITHDRAWN", "DISMISSED", "WRITTEN_OFF", "CLOSED",
];
const AGEING_BUCKETS = ["0-30", "31-60", "61-90", "91-180", "181-365", "365+"];
const RECOVERY_STATUSES = ["NOT_RECOVERED", "PARTIALLY_RECOVERED", "FULLY_RECOVERED"];
export function Reports() {
    const [searchParams] = useSearchParams();
    const requestedType = searchParams.get("type");
    const [reportList, setReportList] = useState([]);
    const [activeType, setActiveType] = useState(null);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [legalFirms, setLegalFirms] = useState([]);
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState(() => {
        const companyId = searchParams.get("companyId");
        return companyId ? { companyId } : {};
    });
    // Collapsible sections. On a phone the report list and filters fill the
    // first screen, so they start folded and fold again once used; on desktop
    // they start open.
    const [listOpen, setListOpen] = useState(() => !isNarrow());
    const [filtersOpen, setFiltersOpen] = useState(() => !isNarrow());
    const [resultOpen, setResultOpen] = useState(true);
    const activeFilterCount = Object.values(filters).filter(Boolean).length;
    const activeLabel = reportList.find((r) => r.type === activeType)?.label;
    useEffect(() => {
        api.get("/reports").then((r) => {
            setReportList(r.items);
            const preselect = requestedType && r.items.some((i) => i.type === requestedType) ? requestedType : r.items[0]?.type;
            if (preselect)
                setActiveType(preselect);
        });
        api.get("/companies").then((r) => setCompanies(r.items));
        api.get("/customers").then((r) => setCustomers(r.items));
        api.get("/legal-firms").then((r) => setLegalFirms(r.items));
        api.get("/users").then((r) => setUsers(r.items));
    }, []);
    function runReport() {
        if (!activeType)
            return;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
        api
            .get(`/reports/${activeType}?${params.toString()}`)
            .then(setReport)
            .catch(() => setError("Couldn't load this report."))
            .finally(() => setLoading(false));
    }
    useEffect(runReport, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps
    function setFilter(key, value) {
        setFilters((f) => ({ ...f, [key]: value }));
    }
    function exportAs(format) {
        if (!activeType)
            return;
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        downloadReportExport(activeType, format, params).catch(() => setError("Export failed."));
    }
    return (<div>
      <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.6rem", margin: "0 0 18px" }}>
        Reports
      </h1>

      <div className="lg-stack" style={{ display: "grid", gridTemplateColumns: "220px minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
        <Section title="Report" summary={activeLabel} open={listOpen} onToggle={() => setListOpen((o) => !o)}>
          {reportList.map((r) => (<div key={r.type} onClick={() => { setActiveType(r.type); if (isNarrow()) setListOpen(false); }} style={{
                padding: "9px 12px",
                borderRadius: 7,
                fontSize: "0.83rem",
                fontWeight: activeType === r.type ? 700 : 500,
                background: activeType === r.type ? "var(--accent-soft)" : "transparent",
                color: activeType === r.type ? "var(--accent-ink)" : "var(--ink-soft)",
                cursor: "pointer",
                marginBottom: 2,
            }}>
              {r.label}
            </div>))}
        </Section>

        <div>
          <Section
            title="Filters"
            summary={activeFilterCount ? `${activeFilterCount} active` : "None"}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
            style={{ marginBottom: 16 }}
          >
            {/* auto-fill, not repeat(4, 1fr): four selects don't fit beside the
                report list on a laptop/tablet, and a bare 1fr can't shrink below
                its widest option, so the row ran off the right edge. */}
            <div className="lg-pair" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 8 }}>
              <select value={filters.year ?? ""} onChange={(e) => setFilter("year", e.target.value)} style={inputStyle}>
                <option value="">All years</option>
                {[2024, 2025, 2026].map((y) => (<option key={y} value={y}>{y}</option>))}
              </select>
              <input type="date" placeholder="From" value={filters.dateFrom ?? ""} onChange={(e) => setFilter("dateFrom", e.target.value)} style={inputStyle}/>
              <input type="date" placeholder="To" value={filters.dateTo ?? ""} onChange={(e) => setFilter("dateTo", e.target.value)} style={inputStyle}/>
              <select value={filters.caseStatus ?? ""} onChange={(e) => setFilter("caseStatus", e.target.value)} style={inputStyle}>
                <option value="">All statuses</option>
                {CASE_STATUSES.map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
              </select>
              <select value={filters.companyId ?? ""} onChange={(e) => setFilter("companyId", e.target.value)} style={inputStyle}>
                <option value="">All companies</option>
                {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
              <select value={filters.customerId ?? ""} onChange={(e) => setFilter("customerId", e.target.value)} style={inputStyle}>
                <option value="">All customers</option>
                {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
              <select value={filters.legalFirmId ?? ""} onChange={(e) => setFilter("legalFirmId", e.target.value)} style={inputStyle}>
                <option value="">All legal firms</option>
                {legalFirms.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
              </select>
              <select value={filters.responsibleUserId ?? ""} onChange={(e) => setFilter("responsibleUserId", e.target.value)} style={inputStyle}>
                <option value="">All responsible persons</option>
                {users.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
              <select value={filters.ageingBucket ?? ""} onChange={(e) => setFilter("ageingBucket", e.target.value)} style={inputStyle}>
                <option value="">All ageing</option>
                {AGEING_BUCKETS.map((b) => (<option key={b} value={b}>{b} days</option>))}
              </select>
              <select value={filters.recoveryStatus ?? ""} onChange={(e) => setFilter("recoveryStatus", e.target.value)} style={inputStyle}>
                <option value="">All recovery statuses</option>
                {RECOVERY_STATUSES.map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
              </select>
              <button onClick={() => { runReport(); if (isNarrow()) setFiltersOpen(false); }} style={runBtn}>Apply Filters</button>
            </div>
          </Section>

          {error && <div style={{ color: "var(--st-risk)", marginBottom: 12 }}>{error}</div>}
          {loading && <div style={{ color: "var(--ink-faint)" }}>Loading…</div>}

          {report && !loading && (<div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: resultOpen ? "1px solid var(--line-soft)" : "none", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <button type="button" onClick={() => setResultOpen((o) => !o)} aria-expanded={resultOpen} style={{ ...toggleBtn, flex: "1 1 240px" }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 700, fontSize: "0.95rem", color: "var(--ink)" }}>{report.title}</span>
                    <span style={{ display: "block", fontSize: "0.78rem", color: "var(--ink-faint)", marginTop: 2 }}>
                      {report.filtersSummary}{!resultOpen && ` · ${report.rows.length} rows`}
                    </span>
                  </span>
                  <Chevron open={resultOpen}/>
                </button>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => exportAs("xlsx")} style={exportBtn}>Export Excel</button>
                  <button onClick={() => exportAs("pdf")} style={exportBtn}>Export PDF</button>
                </div>
              </div>

              {resultOpen && (<>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-2)" }}>
                      {report.columns.map((c) => (<th key={c.key} style={{ padding: "8px 14px", textAlign: c.align ?? "left", fontSize: "0.68rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>
                          {c.label}
                        </th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row, i) => (<tr key={i} style={{ borderTop: "1px solid var(--line-soft)" }}>
                        {report.columns.map((c) => (<td key={c.key} style={{ padding: "8px 14px", textAlign: c.align ?? "left" }} className={c.align === "right" ? "mono tabnum" : ""}>
                            {c.key === "caseCode" && row.caseId ? (<Link to={`/legal-cases/cases/${row.caseId}`} style={{ color: "var(--accent-ink)", fontWeight: 600 }}>
                                {row[c.key]}
                              </Link>) : (row[c.key])}
                          </td>))}
                      </tr>))}
                    {report.totals && (<tr style={{ borderTop: "2px solid var(--line)", fontWeight: 700 }}>
                        {report.columns.map((c) => (<td key={c.key} style={{ padding: "8px 14px", textAlign: c.align ?? "left" }} className={c.align === "right" ? "mono tabnum" : ""}>
                            {report.totals[c.key]}
                          </td>))}
                      </tr>)}
                  </tbody>
                </table>
                {report.rows.length === 0 && (<div style={{ padding: 24, textAlign: "center", color: "var(--ink-faint)", fontSize: "0.85rem" }}>No rows match these filters.</div>)}
              </div>

              {report.reconciliation && report.reconciliation.length > 0 && (<div style={{ padding: "14px 18px", borderTop: "1px solid var(--line-soft)", display: "flex", gap: 24 }}>
                  {report.reconciliation.map((r) => (<div key={r.label}>
                      <div className="mono" style={{ fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>{r.label}</div>
                      <div className="mono tabnum" style={{ fontSize: "0.9rem", fontWeight: 700, marginTop: 2 }}>{r.value}</div>
                    </div>))}
                </div>)}
              </>)}
            </div>)}
        </div>
      </div>
    </div>);
}
// The module's own width, same as the @container rules in legal.css — the
// screen width overstates the room when the app's left navigation is open.
function isNarrow() {
    const root = typeof document !== "undefined" && document.querySelector(".legal-root");
    return !!root && root.clientWidth <= 760;
}
function Chevron({ open }) {
    return (<span aria-hidden="true" style={{
            flex: "none",
            fontSize: "0.9rem",
            color: "var(--ink-faint)",
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "none",
        }}>▾</span>);
}
// A card whose header folds its body away; the summary stays visible when
// folded so the user can see what is selected without opening it.
function Section({ title, summary, open, onToggle, style, children }) {
    return (<div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, ...style }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={{ ...toggleBtn, padding: "12px 14px" }}>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--ink-faint)" }}>{title}</span>
          {summary && <span style={{ display: "block", marginTop: 2, fontSize: "0.86rem", fontWeight: 600, color: "var(--ink)" }}>{summary}</span>}
        </span>
        <Chevron open={open}/>
      </button>
      {open && <div style={{ padding: "0 12px 12px" }}>{children}</div>}
    </div>);
}
const toggleBtn = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    width: "100%",
    background: "none",
    border: "none",
    padding: 0,
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
};
const inputStyle = {
    width: "100%",
    minWidth: 0,
    border: "1px solid var(--line)",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: "0.8rem",
    fontFamily: "inherit",
};
const runBtn = {
    background: "var(--brand)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "8px 14px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
};
const exportBtn = {
    border: "1px solid var(--line)",
    background: "var(--surface)",
    color: "var(--ink-soft)",
    fontWeight: 600,
    fontSize: "0.78rem",
    padding: "7px 12px",
    borderRadius: 8,
    cursor: "pointer",
};
